import { useMemo } from "react";

import { API_URLS } from "shared/constants";

import useHttpActions from "shared/services/api/core/useHttpActions";
import type {
	BackendEntityType,
	PaginationParamsModel,
	PaginationTokenType,
	PersonObject,
	PostModel,
	ReportedPersonModel,
	ReportedPostModel
} from "shared/types";
import {
	paginationTokenResolver,
	personObject,
	postResolver,
	reportedPersonResolver,
	reportedPostResolver
} from "shared/types";

import { BaseConverter } from "./converters";

import type { AdminBaseEntityModel, AdminEntityActions, CronJobModel, QuickLinkModel } from "../types/AdminTypes";
import { adminBaseEntityObject, cronJobResolver, quickLinkResolver } from "../types/AdminTypes";

const useAdminApiService = () => {
	const actions = useHttpActions();

	return useMemo(
		() => ({
			listUsers: async ({ search, after }: { search?: string } & PaginationParamsModel) => {
				return BaseConverter.convert<{ people: PersonObject[]; nextToken: PaginationTokenType }>(
					await actions.get(
						API_URLS.ADMIN.LIST_USERS,
						{
							search,
							after
						},
						{
							resolvers: [
								{
									resolver: personObject,
									fields: ["people"]
								},
								{
									fields: ["nextToken"],
									resolver: paginationTokenResolver
								}
							]
						}
					)
				);
			},
			assignUserRoles: async (admins: { add?: { id: string }[]; remove?: { id: string }[] }) => {
				return BaseConverter.convert(
					await actions.post(API_URLS.ADMIN.ASSIGN_USER_ROLES, {
						admins
					})
				);
			},
			listReportedUsers: async ({ limit, after }: PaginationParamsModel) => {
				return BaseConverter.convert<{ people: ReportedPersonModel[]; nextToken: PaginationTokenType }>(
					await actions.get(
						API_URLS.ADMIN.LIST_REPORTED_USERS,
						{
							limit,
							after
						},
						{
							resolvers: [
								{
									resolver: reportedPersonResolver,
									fields: ["people"]
								},
								{
									fields: ["nextToken"],
									resolver: paginationTokenResolver
								}
							]
						}
					)
				);
			},
			banUsers: async (personIds: string[]) => {
				return BaseConverter.convert<{ success: boolean }>(
					await actions.post(API_URLS.ADMIN.BAN_USERS, {
						personIds
					})
				);
			},
			ignoreReportedUsers: async (personIds: string[]) => {
				return BaseConverter.convert<{ success: boolean }>(
					await actions.post(API_URLS.ADMIN.IGNORE_REPORTED_USERS, {
						personIds
					})
				);
			},
			listReportedPosts: async ({ limit, after }: PaginationParamsModel) => {
				return BaseConverter.convert<{ posts: ReportedPostModel[]; nextToken: PaginationTokenType }>(
					await actions.get(
						API_URLS.ADMIN.LIST_REPORTED_POSTS,
						{
							limit,
							after
						},
						{
							resolvers: [
								{
									resolver: reportedPostResolver,
									fields: ["posts"]
								},
								{
									fields: ["nextToken"],
									resolver: paginationTokenResolver
								}
							]
						}
					)
				);
			},
			deleteReportedPosts: async (postIds: string[]) => {
				return BaseConverter.convert<{ success: boolean }>(
					await actions.post(API_URLS.ADMIN.DELETE_REPORTED_POSTS, {
						postIds
					})
				);
			},
			ignoreReportedPosts: async (postIds: string[]) => {
				return BaseConverter.convert<{ success: boolean }>(
					await actions.post(API_URLS.ADMIN.IGNORE_REPORTED_POSTS, {
						postIds
					})
				);
			},
			listClubs: async ({ search, after, before }: { search?: string } & PaginationParamsModel) => {
				return BaseConverter.convert<{
					clubs: AdminBaseEntityModel[];
					nextToken: PaginationTokenType;
					previousToken: PaginationTokenType;
				}>(
					await actions.get(
						API_URLS.ADMIN.LIST_CLUBS,
						{
							search,
							after,
							before
						},
						{
							resolvers: [
								{
									resolver: adminBaseEntityObject,
									fields: ["clubs"]
								},
								{
									fields: ["nextToken"],
									resolver: paginationTokenResolver
								},
								{
									fields: ["previousToken"],
									resolver: paginationTokenResolver
								}
							]
						}
					)
				);
			},
			manageClub: async (clubId: string, action: AdminEntityActions) => {
				return BaseConverter.convert<{ success: boolean }>(
					await actions.post(API_URLS.ADMIN.MANAGE_CLUB, {
						clubId,
						action
					})
				);
			},
			listLeagues: async ({ search, after, before }: { search?: string } & PaginationParamsModel) => {
				return BaseConverter.convert<{
					leagues: AdminBaseEntityModel[];
					nextToken: PaginationTokenType;
					previousToken: PaginationTokenType;
				}>(
					await actions.get(
						API_URLS.ADMIN.LIST_LEAGUES,
						{
							search,
							after,
							before
						},
						{
							resolvers: [
								{
									resolver: adminBaseEntityObject,
									fields: ["leagues"]
								},
								{
									fields: ["nextToken"],
									resolver: paginationTokenResolver
								},
								{
									fields: ["previousToken"],
									resolver: paginationTokenResolver
								}
							]
						}
					)
				);
			},
			manageLeague: async (leagueId: string, action: AdminEntityActions) => {
				return BaseConverter.convert<{ success: boolean }>(
					await actions.post(API_URLS.ADMIN.MANAGE_LEAGUE, {
						leagueId,
						action
					})
				);
			},
			listFacilities: async ({ search, after, before }: { search?: string } & PaginationParamsModel) => {
				return BaseConverter.convert<{
					facilities: AdminBaseEntityModel[];
					nextToken: PaginationTokenType;
					previousToken: PaginationTokenType;
				}>(
					await actions.get(
						API_URLS.ADMIN.LIST_FACILITIES,
						{
							search,
							after,
							before
						},
						{
							resolvers: [
								{
									resolver: adminBaseEntityObject,
									fields: ["facilities"]
								},
								{
									fields: ["nextToken"],
									resolver: paginationTokenResolver
								},
								{
									fields: ["previousToken"],
									resolver: paginationTokenResolver
								}
							]
						}
					)
				);
			},
			manageFacility: async (facilityId: string, action: AdminEntityActions) => {
				return BaseConverter.convert<{ success: boolean }>(
					await actions.post(API_URLS.ADMIN.MANAGE_FACILITY, {
						facilityId,
						action
					})
				);
			},
			listRefOrgs: async ({ search, after, before }: { search?: string } & PaginationParamsModel) => {
				return BaseConverter.convert<{
					refOrgs: AdminBaseEntityModel[];
					nextToken: PaginationTokenType;
					previousToken: PaginationTokenType;
				}>(
					await actions.get(
						API_URLS.ADMIN.LIST_REF_ORGS,
						{
							search,
							after,
							before
						},
						{
							resolvers: [
								{
									resolver: adminBaseEntityObject,
									fields: ["refOrgs"]
								},
								{
									fields: ["nextToken"],
									resolver: paginationTokenResolver
								},
								{
									fields: ["previousToken"],
									resolver: paginationTokenResolver
								}
							]
						}
					)
				);
			},
			manageRefOrg: async (refOrgId: string, action: AdminEntityActions) => {
				return BaseConverter.convert<{ success: boolean }>(
					await actions.post(API_URLS.ADMIN.MANAGE_REF_ORG, {
						refOrgId,
						action
					})
				);
			},
			listQuickLinks: async () => {
				return BaseConverter.convert<{
					quickLinks: QuickLinkModel[];
				}>(
					await actions.get(API_URLS.ADMIN.LIST_QUICK_LINKS, null, {
						resolvers: [
							{
								resolver: quickLinkResolver,
								fields: ["quickLinks"]
							}
						]
					})
				);
			},
			updateQuickLinks: async ({
				add,
				remove,
				reorder
			}: {
				add?: Partial<QuickLinkModel>[];
				remove?: { id: string }[];
				reorder?: { id: string; newOrder: number };
			}) => {
				return BaseConverter.convert<{ success: boolean }>(
					await actions.post(API_URLS.ADMIN.UPDATE_QUICK_LINKS, {
						add,
						remove,
						reorder
					})
				);
			},
			listPinnedPosts: async () => {
				return BaseConverter.convert<{
					posts: Partial<PostModel>[];
				}>(
					await actions.get(API_URLS.ADMIN.LIST_PINNED_POSTS, null, {
						resolvers: [
							{
								resolver: postResolver.partial(),
								fields: ["posts"]
							}
						]
					})
				);
			},
			updatePinnedPosts: async ({ add, remove }: { add?: { id: string }[]; remove?: { id: string }[] }) => {
				return BaseConverter.convert<{ success: boolean }>(
					await actions.post(API_URLS.ADMIN.UPDATE_PINNED_POSTS, {
						add,
						remove
					})
				);
			},
			createPushNotification: async (params: {
				title: string;
				text: string;
				entityId: string;
				entityType: BackendEntityType;
			}) => {
				return BaseConverter.convert<{ success: boolean }>(
					await actions.post(API_URLS.ADMIN.CREATE_PUSH_NOTIFICATION, params)
				);
			},
			listCronJobs: async () => {
				return BaseConverter.convert<{
					crons: CronJobModel[];
				}>(
					await actions.get(API_URLS.ADMIN.LIST_CRON_JOBS, null, {
						resolvers: [
							{
								resolver: cronJobResolver,
								fields: ["crons"]
							}
						]
					})
				);
			},
			runCronNow: async (cronId: string) => {
				return BaseConverter.convert<{ success: boolean }>(
					await actions.post(API_URLS.ADMIN.LAUNCH_CRON_JOB, {
						id: cronId
					})
				);
			},
			restartCron: async (cronId: string) => {
				return BaseConverter.convert<{ success: boolean }>(
					await actions.post(API_URLS.ADMIN.RESTART_CRON_JOB, {
						id: cronId
					})
				);
			}
		}),
		[actions]
	);
};

export default useAdminApiService;
